import { memo, useEffect, useRef } from "react";
import clsx from "clsx";
import type { POI } from "@poi-planner/shared";
import type { ResultsCardHeight } from "../../store/uiStore";
import { useLibraryStore } from "../../store/libraryStore";
import { hasUsableCoordinates } from "../../utils/dayMapPois";
import { formatDistance, formatTimeBudget } from "../../utils/format";
import { resolveTagNames } from "../../utils/tagDisplay";
import { Button } from "../Button";
import { PoiDetails } from "../PoiDetails";
import { useSheetDrag, type SheetSnap } from "./useSheetDrag";

/**
 * Heights in px: the tab left when collapsed, a short peek, and a tall panel
 * with full details (fitted below the toolbar). Full height reaches the
 * toolbar, so it is measured; the fallback is used until then.
 */
export const RESULTS_COLLAPSED_HEIGHT = 44;
export const RESULTS_PEEK_HEIGHT = 248;
export const RESULTS_TALL_HEIGHT = 550;
export const RESULTS_FULL_FALLBACK_HEIGHT = 720;

/** The next height up and down from each; the tab is the Planner's floor. */
const STEP_UP: Partial<Record<ResultsCardHeight, ResultsCardHeight>> = {
  collapsed: "peek",
  peek: "tall",
  tall: "full",
};
const STEP_DOWN: Partial<Record<ResultsCardHeight, ResultsCardHeight>> = {
  tall: "peek",
  full: "tall",
};

export interface ResultCardActions {
  /** Planner view only: select the POI and switch to the Map view. */
  onShowOnMap?: (poiId: string) => void;
  /** Omitted when the trip is read-only. */
  onSchedule?: (poi: POI) => void;
  onSetTarget?: (poiId: string | null) => void;
  onTogglePin?: (poiId: string) => void;
  /** Opens the POI editor (or viewer when it isn't editable). */
  onOpen: (poi: POI) => void;
  canEditPoi: (poi: POI) => boolean;
}

interface ResultsCarouselProps extends ResultCardActions {
  pois: POI[];
  totalPoiCount: number;
  /** "Unscheduled · Kyoto · by distance" */
  subtitle: string;
  height: ResultsCardHeight;
  /** Whether the card may collapse to its header bar (Planner view). */
  collapsible: boolean;
  /** Rendered height in px, after fitting the full card to the screen. */
  pixelHeight: number;
  /** Heights the card can be dragged between, in px. */
  snaps: SheetSnap<ResultsCardHeight>[];
  onHeightChange: (height: ResultsCardHeight) => void;
  /** Opens every row of the full list (shared with the desktop Sidebar). */
  expandAll: boolean;
  onExpandAllChange: (expandAll: boolean) => void;
  selectedPoiId: string | null;
  onSelectPoi: (poiId: string | null) => void;
  scheduledCounts: Map<string, number>;
  distanceMap: Map<string, number>;
  targetPoiId: string | null;
  pinnedPoiIds: Set<string>;
  /** Omitted offline or read-only. */
  onCreate?: () => void;
  onClearFilters: () => void;
}

/**
 * The phone results card: a bottom overlay whose POIs scroll sideways as
 * compact cards at peek height and detailed cards at tall height, both with
 * the Planner or Map still in view. At full height it reaches the toolbar
 * as a vertical list, for browsing without them (and offline); like the
 * desktop Sidebar, the selected row shows its details, or every row with
 * Expand all. Selection only ever changes
 * through a tap — never from the scroll position — and a selection made
 * elsewhere (a map pin) scrolls its card into view.
 */
export function ResultsCarousel({
  pois,
  totalPoiCount,
  subtitle,
  height,
  collapsible,
  pixelHeight,
  snaps,
  onHeightChange,
  expandAll,
  onExpandAllChange,
  selectedPoiId,
  onSelectPoi,
  scheduledCounts,
  distanceMap,
  targetPoiId,
  pinnedPoiIds,
  onCreate,
  onClearFilters,
  ...actions
}: ResultsCarouselProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const tall = height === "tall";
  const full = height === "full";
  const stepUp = STEP_UP[height];
  const stepDown = STEP_DOWN[height];
  // The grab handle steps up a height at a time, and from full back to peek
  const cycleHeight = () => onHeightChange(stepUp ?? "peek");
  // Dragging the handle/header (or the collapsed tab) resizes the card live,
  // then snaps to the nearest height on release.
  const drag = useSheetDrag({
    height: pixelHeight,
    snaps,
    onSnap: onHeightChange,
  });
  const dragging = drag.dragHeight !== null;

  // Keep the selected card in view when it changes and when the card
  // resizes; wait a frame so the cards have their new sizes. The list only
  // scrolls when the row isn't already wholly on screen.
  useEffect(() => {
    const row = rowRef.current;
    if (!selectedPoiId || !row) return;
    const frame = requestAnimationFrame(() => {
      const card = row.querySelector<HTMLElement>(
        `[data-poi-id="${selectedPoiId}"]`,
      );
      if (!card) return;
      const style = getComputedStyle(row);
      if (height !== "full") {
        const inset = parseFloat(style.paddingLeft) || 0;
        row.scrollTo?.({ left: card.offsetLeft - inset, behavior: "smooth" });
        return;
      }
      const onScreen =
        card.offsetTop >= row.scrollTop &&
        card.offsetTop + card.offsetHeight <= row.scrollTop + row.clientHeight;
      if (onScreen) return;
      const inset = parseFloat(style.paddingTop) || 0;
      row.scrollTo?.({ top: card.offsetTop - inset, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedPoiId, height]);

  // Collapsed, the card shrinks to a tab on the bottom edge so the Planner
  // gets nearly the whole screen; pressing it brings the results back.
  if (height === "collapsed" && !dragging) {
    return (
      <Button
        variant="unstyled"
        onPointerDown={drag.onPointerDown}
        onClickCapture={drag.onClickCapture}
        aria-label={`Show results, ${pois.length} ${pois.length === 1 ? "POI" : "POIs"}`}
        aria-expanded={false}
        data-testid="results-tab"
        onClick={() => onHeightChange("peek")}
        className="absolute bottom-0 left-1/2 z-30 flex h-11 -translate-x-1/2 cursor-pointer touch-none items-center gap-1.5 rounded-t-xl border border-b-0 border-border bg-surface pr-3 pl-4 text-sm font-bold text-primary shadow-lg"
      >
        <span className="tabular-nums">
          {pois.length} {pois.length === 1 ? "POI" : "POIs"}
        </span>
        <span className="material-symbols-outlined text-lg" aria-hidden>
          expand_less
        </span>
      </Button>
    );
  }

  return (
    <section
      aria-label="Results"
      data-testid="results-card"
      data-height={height}
      data-dragging={dragging || undefined}
      className={clsx(
        "absolute inset-x-0 bottom-0 z-30 flex flex-col overflow-hidden bg-surface pb-[env(safe-area-inset-bottom)]",
        // Full height meets the toolbar's own border
        !full && "rounded-t-2xl border-t border-border shadow-lg",
        !dragging && "transition-[height] duration-200",
      )}
      // Runtime geometry: fitted to the screen, or following a drag
      style={{ height: drag.dragHeight ?? pixelHeight }}
    >
      <div
        className="flex shrink-0 touch-none flex-col"
        onPointerDown={drag.onPointerDown}
        onClickCapture={drag.onClickCapture}
      >
        <Button
          variant="unstyled"
          aria-label={
            full ? "Show less of the results" : "Show more of the results"
          }
          aria-expanded
          onClick={cycleHeight}
          className="flex h-5 w-full shrink-0 cursor-pointer items-end justify-center"
        >
          <span className="h-1 w-10 rounded-full bg-border" aria-hidden />
        </Button>

        <div className="flex shrink-0 items-center gap-1 px-4 pt-1">
          <div className="min-w-0 flex-1">
            <h2 className="m-0 text-base font-bold text-primary">
              {pois.length} {pois.length === 1 ? "POI" : "POIs"}
            </h2>
            <p className="m-0 truncate text-xs text-secondary">{subtitle}</p>
          </div>
          {onCreate && (
            <Button
              variant="primary"
              size="sm"
              icon="add"
              className="min-h-11"
              onClick={onCreate}
            >
              Create
            </Button>
          )}
          {full && (
            <Button
              variant="icon"
              size="lg"
              icon={expandAll ? "unfold_less" : "unfold_more"}
              selected={expandAll}
              aria-label="Expand all POIs"
              onClick={() => onExpandAllChange(!expandAll)}
            />
          )}
          {height === "peek" && collapsible && (
            <Button
              variant="icon"
              size="lg"
              icon="keyboard_arrow_down"
              aria-label="Collapse results"
              onClick={() => onHeightChange("collapsed")}
            />
          )}
          {stepDown && (
            <Button
              variant="icon"
              size="lg"
              icon="expand_more"
              aria-label="Shrink results"
              onClick={() => onHeightChange(stepDown)}
            />
          )}
          {stepUp && (
            <Button
              variant="icon"
              size="lg"
              icon="expand_less"
              aria-label="Expand results"
              onClick={() => onHeightChange(stepUp)}
            />
          )}
        </div>
      </div>

      {pois.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
          <p className="m-0 text-sm font-semibold text-secondary">
            {totalPoiCount === 0 ? "No POIs yet" : "No POIs match"}
          </p>
          {totalPoiCount > 0 && (
            <Button variant="secondary" size="sm" onClick={onClearFilters}>
              Clear filters
            </Button>
          )}
        </div>
      ) : (
        <div
          ref={rowRef}
          data-testid={full ? "results-list" : "results-row"}
          className={clsx(
            "relative flex min-h-0 flex-1 px-4 pt-2 pb-3",
            full
              ? "flex-col gap-2 overflow-y-auto overscroll-contain"
              : "scrollbar-none snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto overscroll-x-contain",
          )}
        >
          {pois.map((poi, index) => (
            <ResultCard
              key={poi.id}
              poi={poi}
              number={index + 1}
              layout={full ? "list" : "carousel"}
              detailed={full ? expandAll || poi.id === selectedPoiId : tall}
              selected={poi.id === selectedPoiId}
              onSelect={onSelectPoi}
              onExpand={(poiId) => {
                onSelectPoi(poiId);
                onHeightChange("tall");
              }}
              scheduledCount={scheduledCounts.get(poi.id) ?? 0}
              distance={targetPoiId ? distanceMap.get(poi.id) : undefined}
              isTargeted={poi.id === targetPoiId}
              isPinned={pinnedPoiIds.has(poi.id)}
              {...actions}
            />
          ))}
        </div>
      )}
    </section>
  );
}

interface ResultCardProps extends ResultCardActions {
  poi: POI;
  number: number;
  /** A sideways carousel card (peek, tall) or a list row (full height). */
  layout: "carousel" | "list";
  /** Shows the POI's details: a tall card, or an open list row. */
  detailed: boolean;
  selected: boolean;
  onSelect: (poiId: string | null) => void;
  onExpand: (poiId: string) => void;
  scheduledCount: number;
  distance?: number;
  isTargeted: boolean;
  isPinned: boolean;
}

const ResultCard = memo(function ResultCard({
  poi,
  number,
  layout,
  detailed,
  selected,
  onSelect,
  onExpand,
  scheduledCount,
  distance,
  isTargeted,
  isPinned,
  onShowOnMap,
  onSchedule,
  onSetTarget,
  onTogglePin,
  onOpen,
  canEditPoi,
}: ResultCardProps) {
  const managedTags = useLibraryStore((state) => state.tags);
  const tagNames = resolveTagNames(poi.tags, managedTags);
  const hasCoords = hasUsableCoordinates(poi);
  const canEdit = canEditPoi(poi);
  const list = layout === "list";
  // Hints that the detailed card or row has more to show
  const detailsHint =
    Boolean(poi.notes?.trim()) || poi.sources.some((s) => s.url?.trim());

  return (
    <article
      data-poi-id={poi.id}
      aria-label={`${number}. ${poi.name}`}
      className={clsx(
        "flex shrink-0 flex-col overflow-hidden rounded-xl border transition-shadow",
        list ? "w-full" : clsx("snap-start", detailed ? "w-80" : "w-66"),
        selected
          ? "border-selection bg-selection-bg ring-2 ring-selection/30"
          : "border-border bg-surface",
      )}
    >
      <Button
        variant="unstyled"
        // A list row opens with its selection; tapping it again deselects
        {...(list
          ? {
              "aria-expanded": detailed,
              "aria-label": `Details for ${poi.name}`,
            }
          : { "aria-pressed": selected, "aria-label": `Select ${poi.name}` })}
        onClick={() => onSelect(list && selected ? null : poi.id)}
        className="flex w-full min-w-0 shrink-0 cursor-pointer items-start gap-2.5 px-3 pt-3 pb-1.5 text-left"
      >
        <span
          className={clsx(
            "grid size-6 shrink-0 place-items-center rounded-full text-xs font-bold tabular-nums text-white",
            selected ? "bg-selection" : "bg-primary",
          )}
          aria-hidden
        >
          {number}
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={clsx(
              "block break-words text-sm font-bold",
              !(list && detailed) &&
                (list || detailed ? "line-clamp-2" : "line-clamp-1"),
              selected ? "text-selection-text" : "text-primary",
            )}
          >
            {poi.name}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-secondary">
            <span className="inline-flex items-center gap-0.5">
              <span className="material-symbols-outlined text-sm" aria-hidden>
                schedule
              </span>
              {formatTimeBudget(poi.timeBudget)}
            </span>
            {distance !== undefined && (
              <span className="rounded bg-info/10 px-1.5 font-bold text-info">
                {formatDistance(distance)}
              </span>
            )}
            {scheduledCount > 0 && (
              <span className="rounded bg-success-bg px-1.5 font-semibold text-success">
                On calendar{scheduledCount > 1 ? ` ×${scheduledCount}` : ""}
              </span>
            )}
            {!hasCoords && (
              <span className="rounded bg-warning-bg px-1.5 font-semibold text-warning">
                No location
              </span>
            )}
            {!detailed && detailsHint && (
              <span
                className="material-symbols-outlined text-sm text-tertiary"
                aria-label="Has details"
              >
                notes
              </span>
            )}
          </span>
        </span>
      </Button>

      {tagNames.length > 0 && (
        <div
          className={clsx(
            "flex shrink-0 gap-1 px-3",
            detailed ? "flex-wrap" : "overflow-hidden",
          )}
        >
          {tagNames.map((tag) => (
            <span
              key={tag}
              className={clsx(
                "shrink-0 rounded-md px-1.5 py-0.5 text-xs font-medium",
                selected
                  ? "bg-surface text-selection-text"
                  : "bg-subtle text-secondary",
              )}
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {detailed ? (
        <div
          className={clsx(
            "px-3 pt-2.5 pb-2",
            // A tall card has a fixed height, so its details scroll
            !list && "min-h-0 flex-1 overflow-y-auto overscroll-contain",
          )}
        >
          <PoiDetails
            poi={poi}
            clampNotes={false}
            canEdit={canEdit}
            onEdit={() => onOpen(poi)}
          />
          <p className="mt-3 mb-0 flex items-center gap-1 text-2xs text-tertiary">
            <span className="material-symbols-outlined text-sm" aria-hidden>
              person
            </span>
            Added by {poi.ownerName ?? "you"}
          </p>
        </div>
      ) : list ? (
        // A closed list row is just its summary; actions come with details
        <div className="h-2.5 shrink-0" />
      ) : (
        <div className="flex-1" />
      )}

      {(detailed || !list) && (
        <div
          className={clsx(
            "flex shrink-0 items-center gap-0.5 border-t px-2 py-0.5",
            selected ? "border-selection/30" : "border-border",
          )}
        >
          {onSchedule && (
            <Button
              variant="secondary"
              size="sm"
              icon="event"
              className="min-h-11"
              aria-label={`${scheduledCount > 0 ? "Schedule again" : "Schedule"} ${poi.name}`}
              onClick={() => onSchedule(poi)}
            >
              {scheduledCount > 0 ? "Schedule again" : "Schedule"}
            </Button>
          )}
          <span className="flex-1" />
          {onShowOnMap && hasCoords && (
            <Button
              variant="icon"
              size="lg"
              icon="map"
              aria-label={`Show ${poi.name} on map`}
              onClick={() => onShowOnMap(poi.id)}
            />
          )}
          {!onShowOnMap && onSetTarget && hasCoords && (
            <Button
              variant="icon"
              size="lg"
              icon="gps_fixed"
              selected={isTargeted}
              aria-label={
                isTargeted
                  ? "Clear proximity target"
                  : `Sort by distance from ${poi.name}`
              }
              onClick={() => onSetTarget(isTargeted ? null : poi.id)}
            />
          )}
          {detailed && onTogglePin && (
            <Button
              variant="icon"
              size="lg"
              icon={isPinned ? "keep" : "keep_off"}
              selected={isPinned}
              aria-label={
                isPinned
                  ? `Manage pin for ${poi.name}`
                  : `Pin ${poi.name} to trip`
              }
              onClick={() => onTogglePin(poi.id)}
            />
          )}
          {detailed && (
            <Button
              variant="icon"
              size="lg"
              icon={canEdit ? "edit" : "visibility"}
              aria-label={canEdit ? `Edit ${poi.name}` : `View ${poi.name}`}
              onClick={() => onOpen(poi)}
            />
          )}
          {!list && !detailed && (
            <Button
              variant="icon"
              size="lg"
              icon="open_in_full"
              aria-label={`Expand ${poi.name}`}
              onClick={() => onExpand(poi.id)}
            />
          )}
        </div>
      )}
    </article>
  );
});
